// Print the POS receipt as a PDF instead of via the browser's HTML print path.
//
// Why: Chrome's HTML printing stamps the page URL, date and "1/2" page numbers
// into the margins (its "Headers and footers" option) and applies the user's
// margin setting — neither of which a web page can control. Printing a PDF
// bypasses all of that: the roll size and margins are baked into the document,
// so the receipt always comes out clean and full-width, with no per-user setup.
//
// Strategy: rasterize the receipt element with html2canvas, then emit a single
// jsPDF page sized exactly to the roll width x the content height (a continuous
// receipt, never split into "1/2 · 2/2"), and hand it to the print dialog.

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const MM_PER_PX = 25.4 / 96; // CSS px -> mm at 96dpi

function rollWidthMm(paperWidth) {
  if (paperWidth === "58mm") return 58;
  if (paperWidth === "A4") return 210;
  return 80;
}

/**
 * Render `element` to a roll-sized PDF and open the print dialog for it.
 * Returns silently if anything is missing.
 */
export async function printReceiptPdf({ element, paperWidth = "80mm", filename = "receipt.pdf", download = false }) {
  if (!element) throw new Error("Nothing to print");

  const canvas = await html2canvas(element, {
    scale: 3,              // crisp on thermal printers
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  });

  const widthMm = rollWidthMm(paperWidth);
  // Keep the on-screen aspect ratio so nothing is stretched.
  const heightMm = (canvas.height / canvas.width) * widthMm;

  const pdf = new jsPDF({
    orientation: heightMm >= widthMm ? "portrait" : "landscape",
    unit: "mm",
    format: [widthMm, heightMm],   // one continuous page = no 1/2, 2/2 split
    compress: true,
  });
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, widthMm, heightMm, undefined, "FAST");

  if (download) {
    pdf.save(filename);
    return;
  }

  // autoPrint makes the PDF open its own print dialog — no browser headers,
  // margins already baked in.
  pdf.autoPrint();
  const url = pdf.output("bloburl");
  const w = window.open(url, "_blank");
  if (!w) {
    // Popup blocked — fall back to downloading so the sale can still be printed.
    pdf.save(filename);
  }
}

export function buildReceiptFilename(transaction) {
  const num = transaction?.transaction_number || transaction?.id || "receipt";
  return `${String(num).replace(/[\\/:*?"<>|]+/g, "")}.pdf`;
}

export { MM_PER_PX };
